import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useParams } from "react-router-dom";
import { fetchPortfolioHistory } from "../../../services/portfolioService";

const TABS = [
  { id: "all", label: "All" },
  { id: "kpi", label: "KPI" },
  { id: "companyNews", label: "Company News" },
  { id: "grade", label: "Grade" },
  { id: "docs", label: "Docs" },
];

export default function HistoryModal({ open, onClose, companyName: companyNameFromContext }) {
  const companyName = (companyNameFromContext || "").trim();
  const [activeTab, setActiveTab] = useState("all");
  const [hideChanges, setHideChanges] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isCancelled = false;

    const loadHistory = async () => {
      if (!open || !companyName.trim()) return;
      setLoading(true);
      setError(null);
      try {
        const data = await fetchPortfolioHistory(companyName);
        if (!isCancelled) {
          setHistoryData(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        if (!isCancelled) {
          setHistoryData([]);
          setError(e?.message || "Failed to load history.");
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadHistory();
    return () => {
      isCancelled = true;
    };
  }, [open, companyName]);

  const renderKpiSection = (entry, showSection) => {
    if (!showSection || !entry.kpi?.length) return null;
    return (
      <Box mb={2}>
        <Typography fontSize={12} fontWeight={600} color="#6B7280" mb={1}>
          KPI
        </Typography>
        <Stack spacing={0.75}>
          {entry.kpi.map((item) => (
            <Box
              key={item.name}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
            >
              <Typography fontSize={13} color="#374151">
                {item.name}
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ minWidth: hideChanges ? 80 : 120, justifyContent: "flex-end" }}
              >
                <Typography fontSize={13} fontWeight={600} color="#111827">
                  {item.value != null && item.value !== "" ? `$${item.value}M` : "—"}
                </Typography>
                {!hideChanges && (
                  <Box sx={{ minWidth: 52, display: "flex", justifyContent: "flex-end" }}>
                    {item.change !== "0%" && (
                      <Chip
                        size="small"
                        label={`${item.change} ${item.direction === "up" ? "↑" : "↓"}`}
                        sx={{
                          height: 22,
                          fontSize: 11,
                          backgroundColor:
                            item.direction === "up" ? "#E7EBC7" : "#F7D5D2",
                          color: "#374151",
                        }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  };

 const renderGradeSection = (entry, showSection) => {
    if (!showSection) return null;

    const grade = entry.grade;
    if (grade == null) return null;
    if (Array.isArray(grade) && grade.length === 0) return null;

    // Backward-compatible: sometimes grade may still be a string.
    if (!Array.isArray(grade)) {
      return (
        <Box
          mb={2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography fontSize={12} fontWeight={600} color="#6B7280">
            Grade
          </Typography>
          <Typography fontSize={14} fontWeight={600} color="#111827">
            {grade}
          </Typography>
        </Box>
      );
    }

    return (
      <Box mb={2}>
        <Typography fontSize={12} fontWeight={600} color="#6B7280" mb={1}>
          Grade
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {grade.map((g, idx) => {
            const value = g?.value ?? g?.grade ?? "—";
            const direction = g?.direction;
            const isUp = direction === "up";
            const chipColor = isUp ? "#E7EBC7" : "#F7D5D2";
            return (

              <>
                 <Typography fontSize={13} fontWeight={600} color="#111827">
                  {value}
                </Typography>
                { !hideChanges && (
                <Chip
                  key={`${value}-${idx}`}
                  size="small"
                  label={`${direction ? ` ${isUp ? "↑" : "↓"}` : ""}`}
                  sx={{
                    height: 22,
                    fontSize: 11,
                    backgroundColor: chipColor,
                    color: "#374151",
                  }}
                  />)
                }
                </>
            );
          })}
        </Stack>
      </Box>
    );
  };

const renderCompanyNewsSection = (entry, showSection) => {
    if (!showSection) return null;

    const news = entry.companyNews;
    if (!news) return null;
    if (Array.isArray(news) && news.length === 0) return null;

    if (!Array.isArray(news)) {
      return (
        <Box mb={2}>
          <Typography fontSize={13} color="#374151" sx={{ lineHeight: 1.5 }}>
            {news}
          </Typography>
        </Box>
      );
    }

    return (
      <Box mb={2}>
        <Typography fontSize={12} fontWeight={600} color="#6B7280" mb={1}>
          Company News
        </Typography>
        <Stack spacing={1}>
          {news.map((line, idx) => (
            <Typography
              key={idx}
              fontSize={13}
              color="#374151"
              sx={{ lineHeight: 1.5 }}
            >
              {line}
            </Typography>
          ))}
        </Stack>
      </Box>
    );
  };

  const renderDocsSection = (entry, showSection) => {
    if (!showSection || !entry.documents?.length) return null;
    return (
      <Box mb={2}>
        <Typography fontSize={12} fontWeight={600} color="#6B7280" mb={1}>
          Documents
        </Typography>
        <Stack spacing={0.75}>
          {entry.documents.map((doc) => (
            <Box
              key={doc.name}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                py: 0.75,
                px: 1.5,
                borderRadius: 1,
                backgroundColor: "#F9FAFB",
                border: "1px solid #E5E7EB",
              }}
            >
              <Typography fontSize={13} color="#111827" noWrap sx={{ flex: 1 }}>
                {doc.name}
              </Typography>
              <Typography fontSize={11} color="#6B7280">
                {doc.uploadedOn}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  };

  const showKpi = activeTab === "all" || activeTab === "kpi";
  const showGrade = activeTab === "all" || activeTab === "grade";
  const showCompanyNews = activeTab === "all" || activeTab === "companyNews";
  const showDocs = activeTab === "all" || activeTab === "docs";

  const hasDataForTab = (entry, tab) => {
    switch (tab) {
      case "all":
        return (
          (entry.kpi && entry.kpi.length > 0) ||
          (entry.grade != null && (!Array.isArray(entry.grade) || entry.grade.length > 0)) ||
          (entry.companyNews && (!Array.isArray(entry.companyNews) || entry.companyNews.length > 0)) ||
          (entry.documents && entry.documents.length > 0)
        );
      case "kpi":
        return entry.kpi && entry.kpi.length > 0;
      case "grade":
        return entry.grade != null && (!Array.isArray(entry.grade) || entry.grade.length > 0);
      case "companyNews":
        return entry.companyNews && (!Array.isArray(entry.companyNews) || entry.companyNews.length > 0);
      case "docs":
        return entry.documents && entry.documents.length > 0;
      default:
        return false;
    }
  };

  const filteredHistoryData = historyData.filter(entry => hasDataForTab(entry, activeTab));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        root: {
          sx: {
            overflow: "visible",
            "& .MuiDrawer-paper": { overflow: "visible" },
          },
        },
        backdrop: {
          sx: {
            backgroundColor: "transparent",
          },
        },
        paper: {
          sx: {
            width: 392,
            height: 900,
            maxWidth: "100%",
            maxHeight: "100vh",
            boxShadow: "0px 0px 12px 2px #5A60661A",
            overflow: "visible",
            borderTopLeftRadius: "16px",
            borderBottomLeftRadius: "16px",
            borderLeft: "1px solid #E5E7EB",
            opacity: 1,
          },
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            color="#111827"
            sx={{ fontFamily: "serif" }}
          >
            History
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={hideChanges}
                  onChange={(e) => setHideChanges(e.target.checked)}
                  size="small"
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#6B7280",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#9CA3AF",
                    },
                  }}
                />
              }
              label={
                <Typography fontSize={13} color="#6B7280">
                  Hide Changes
                </Typography>
              }
            />
            <IconButton onClick={onClose} size="small" aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Tabs */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {TABS.map((tab) => (
            <Chip
              key={tab.id}
              label={tab.label}
              onClick={() => setActiveTab(tab.id)}
              sx={{
                borderRadius: "20px",
                fontWeight: 600,
                fontSize: 13,
                ...(activeTab === tab.id
                  ? {
                      backgroundColor: "#111827",
                      color: "#FFFFFF",
                      "&:hover": { backgroundColor: "#374151" },
                    }
                  : {
                      backgroundColor: "#F3F4F6",
                      color: "#374151",
                      "&:hover": { backgroundColor: "#E5E7EB" },
                    }),
              }}
            />
          ))}
        </Box>

        {/* Content - scrollable */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: 2,
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : error ? (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          ) : filteredHistoryData.length === 0 ? (
            <Typography fontSize={13} color="#6B7280">
              No history found for this company.
            </Typography>
          ) : (
            [...filteredHistoryData].reverse().map((entry, index) => (
              <Box
                key={entry.date + index}
                sx={{
                  mb: 3,
                  pb: 3,
                  borderBottom:
                    index < filteredHistoryData.length - 1
                      ? "1px solid #E5E7EB"
                      : "none",
                }}
              >
                <Typography
                  fontSize={15}
                  fontWeight={700}
                  color="#111827"
                  sx={{ mb: 1.5 }}
                >
                  {entry.date}
                </Typography>
                {renderKpiSection(entry, showKpi)}
                {renderGradeSection(entry, showGrade)}
                {renderCompanyNewsSection(entry, showCompanyNews)}
                {renderDocsSection(entry, showDocs)}
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Drawer>
  );
}